import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 30;            // 30 attempts × 2s = up to 60 sec
const POLL_INTERVAL_MS = 2000;

export default function EnrollSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState({ state: "pending", message: "Verifying your enrollment…" });
  const [syncing, setSyncing] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);

  const runPoll = () => {
    cancelledRef.current = false;
    attemptsRef.current = 0;
    setStatus({ state: "pending", message: "Verifying your enrollment…" });
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelledRef.current) return;
        if (data.payment_status === "paid" || data.paid) {
          await refresh();
          setStatus({ state: "success", message: "Payment successful. Welcome to APM!" });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "error", message: "Payment session expired. Please try again." });
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          setStatus({
            state: "timeout",
            message: "Still verifying with Stripe. You can re-check now, or click 'Sync my access' below.",
          });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelledRef.current) return;
        setStatus({ state: "error", message: e?.response?.data?.detail || "Couldn't verify payment" });
      }
    };
    tick();
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "No session id found in URL" });
      return;
    }
    runPoll();
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const syncAccess = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/payments/sync");
      await refresh();
      if (data.paid) {
        setStatus({ state: "success", message: data.reason || "Payment confirmed — access unlocked." });
        toast.success("Access unlocked");
      } else {
        toast.error(data.reason || "Stripe still shows pending. Try again shortly.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — please try again.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="enroll-success-page">
        {status.state === "pending" && (
          <>
            <Loader2 className="w-10 h-10 text-red-600 animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="enroll-status-text">Verifying enrollment</h1>
            <p className="text-zinc-600 mt-2">{status.message}</p>
            <p className="text-xs font-mono text-zinc-400 mt-4">Attempt {attemptsRef.current} of {MAX_POLLS}</p>
          </>
        )}
        {status.state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-6" />
            <h1 className="font-heading text-4xl font-bold tracking-tight" data-testid="enroll-status-text">You're in.</h1>
            <p className="text-zinc-600 mt-2">{status.message} Every module is now unlocked.</p>
            <button onClick={() => nav("/dashboard")} className="btn-primary mt-8" data-testid="enroll-go-dashboard-btn">
              Go to dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status.state === "timeout" && (
          <>
            <RefreshCw className="w-10 h-10 text-zinc-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="enroll-status-text">Still verifying</h1>
            <p className="text-zinc-600 mt-2 max-w-md mx-auto">{status.message}</p>
            <div className="flex gap-3 justify-center mt-8 flex-wrap">
              <button onClick={runPoll} className="btn-outline" data-testid="enroll-recheck-btn">
                <RefreshCw className="w-4 h-4" /> Re-check now
              </button>
              <button onClick={syncAccess} disabled={syncing} className="btn-primary" data-testid="enroll-sync-btn">
                {syncing ? "Syncing…" : "Sync my access"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] font-mono text-zinc-400 mt-6 max-w-sm mx-auto">
              If neither works, check your email for a Stripe receipt and reply with it — we'll unlock your account manually.
            </p>
          </>
        )}
        {status.state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="enroll-status-text">Something went wrong</h1>
            <p className="text-zinc-600 mt-2">{status.message}</p>
            <div className="flex gap-3 justify-center mt-8">
              <button onClick={syncAccess} disabled={syncing} className="btn-primary" data-testid="enroll-sync-btn">
                {syncing ? "Syncing…" : "Sync my access"}
              </button>
              <Link to="/enroll"><button className="btn-outline">Try again</button></Link>
              <Link to="/dashboard"><button className="btn-outline">Dashboard</button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
